import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import InputPhoneSADC, { getPhoneMarketFromE164 } from "@/components/auth/InputPhoneSADC.jsx";
import Button from "@/components/ui/Button.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { AFRIMED_MARKETS } from "@/config/markets.js";
import InstitutionLifecycleBadge from "../InstitutionLifecycleBadge.jsx";
import { loadInstitutionPatientProfile, updateInstitutionPatientRelationship } from "../institutionService.js";
import { resolvePatientAvatarUrl } from "@/services/patientIdentityService.js";
import InstitutionPatientAnchoredSelect from "./InstitutionPatientAnchoredSelect.jsx";
import { InstitutionPatientPageHeader } from "./InstitutionPatientLayout.jsx";
import InstitutionPatientReadOnlyField from "./InstitutionPatientReadOnlyField.jsx";
import InstitutionProfileCover from "../InstitutionProfileCover.jsx";
import { patientPath, formatPatientStatus } from "./patientUtils.js";
import { formatInstitutionPatientAddress } from "./patientAddress.mjs";
import refreshIcon from "@/icons/afrimed/refresh.svg";
import heartHandshakeIcon from "@/icons/afrimed/heart-handshake.svg";

const MORE_ICON = "/icons/medical-institution/more_vert.svg";
const LINK_ICON = "/icons/clinicians/link.svg";
const NEW_TAB_ICON = "/icons/clinicians/new_tab.svg";
const REFRESH_ICON = refreshIcon;
const MANAGE_ICON = heartHandshakeIcon;
const PATIENT_COVER_LIGHT = "/assets/images/patient_profile_cover.png?v=20260323";
const PATIENT_COVER_DARK = "/assets/images/patient_profile_cover.svg?v=20260323";
const LAWFUL_BASIS_VALUES = [
  "patient_present_intake_authorised",
  "direct_care_registration",
  "referral_documentation_received",
  "emergency_provisional_intake",
  "programme_enrolment_authority",
];

function formatDate(value, fallback = "Not supplied") {
  return value ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(`${value}T12:00:00`)) : fallback;
}

function medicationLabels(values) {
  if (!Array.isArray(values)) return values;
  return values.map((item) => typeof item === "string" ? item : item?.name || item?.label).filter(Boolean).join(", ");
}

function phoneParts(value) {
  const full = String(value || "").trim();
  const market = getPhoneMarketFromE164(full);
  return {
    cc: market?.dialCode || "+27",
    countryIso: market?.iso || "za",
    local: market && full.startsWith(market.dialCode) ? full.slice(market.dialCode.length) : full.replace(/^\+/, ""),
  };
}

function countryFlag(country, phone) {
  const normalized = String(country || "").trim().toLowerCase();
  const market = AFRIMED_MARKETS.find((candidate) => candidate.name.toLowerCase() === normalized) || getPhoneMarketFromE164(phone || "");
  return market ? `/${market.iso}.svg` : "";
}

function PatientSection({ title, children }) {
  return <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{title}</h2><div className="institution-patient-grid">{children}</div></section>;
}

function relationshipDraftFor(relationship) {
  return {
    facilityIds: relationship?.facilities?.map((facility) => facility.id).filter(Boolean) || [],
    lawfulBasisState: relationship?.lawfulBasisState || "",
  };
}

function sameRelationshipDraft(left, right) {
  return left.lawfulBasisState === right.lawfulBasisState
    && left.facilityIds.length === right.facilityIds.length
    && left.facilityIds.every((id) => right.facilityIds.includes(id));
}

function PatientActionIcon({ src }) {
  return <img src={src} alt="" aria-hidden="true" className="institution-patient-overflow-icon" />;
}

export default function InstitutionPatientDetail({ workspace, relationship, onRefresh }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const c = useCallback((key, fallback, values) => t(`institutionPatientDetail.${key}`, fallback, values), [t]);
  const institutionId = workspace?.activeInstitution?.id || "";
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [overflowMenuPosition, setOverflowMenuPosition] = useState(null);
  const [managing, setManaging] = useState(false);
  const [relationshipDraft, setRelationshipDraft] = useState(() => relationshipDraftFor(relationship));
  const [relationshipBusy, setRelationshipBusy] = useState(false);
  const overflowRef = useRef(null);
  const overflowTriggerRef = useRef(null);

  const refreshProfile = useCallback(async ({ announce = false } = {}) => {
    if (!institutionId || !relationship?.id || !relationship?.patientProfileId) return null;
    setProfileLoading(true);
    setProfileError("");
    try {
      const result = await loadInstitutionPatientProfile({ institutionId, relationshipId: relationship.id });
      setProfile(result);
      if (announce) setNotice(c("notices.refreshed", "Patient profile refreshed."));
      return result;
    } catch {
      setProfileError(c("errors.refresh", "Global Patient profile details could not be refreshed. Please try again."));
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, [c, institutionId, relationship?.id, relationship?.patientProfileId]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);
  useEffect(() => { setRelationshipDraft(relationshipDraftFor(relationship)); }, [relationship]);

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const close = (restoreFocus = true) => {
      setOverflowOpen(false);
      if (restoreFocus) requestAnimationFrame(() => overflowTriggerRef.current?.focus());
    };
    const onPointerDown = (event) => { if (!overflowRef.current?.contains(event.target)) close(false); };
    const onKeyDown = (event) => { if (event.key === "Escape") { event.preventDefault(); close(); } };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [overflowOpen]);

  useLayoutEffect(() => {
    if (!overflowOpen || !overflowTriggerRef.current || !window.matchMedia("(max-width: 767px)").matches) {
      setOverflowMenuPosition(null);
      return undefined;
    }
    const positionMenu = () => {
      const rect = overflowTriggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gutter = 16;
      const gap = 8;
      const width = Math.min(320, window.innerWidth - gutter * 2);
      const below = window.innerHeight - rect.bottom - gutter;
      const above = rect.top - gutter;
      const openUpward = below < 184 && above > below;
      const maxHeight = Math.max(160, Math.min(280, (openUpward ? above : below) - gap));
      setOverflowMenuPosition({
        top: openUpward ? Math.max(gutter, rect.top - gap - maxHeight) : rect.bottom + gap,
        right: Math.max(gutter, window.innerWidth - rect.right),
        width,
        maxHeight,
      });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [overflowOpen]);

  const identity = profile?.identity || {};
  const health = profile?.healthProfile || relationship?.healthProfile || {};
  const notSupplied = c("notSupplied", "Not supplied");
  const notRecorded = c("notRecorded", "Not Recorded");
  const patientPhone = identity.phone || relationship?.phone || "";
  const phone = useMemo(() => phoneParts(patientPhone), [patientPhone]);
  const facilities = relationship?.facilities?.map((facility) => facility.name).filter(Boolean).join(", ") || notSupplied;
  const title = identity.fullName || relationship?.fullName || `${relationship?.firstName || ""} ${relationship?.lastName || ""}`.trim() || c("patient", "Patient");
  const identityName = formatInstitutionPatientAddress({ title: identity.title || relationship?.title, lastName: identity.lastName || relationship?.lastName, fullName: title });
  const avatarUrl = resolvePatientAvatarUrl(identity.avatarUrl || relationship?.avatarUrl, identity.avatarPath || relationship?.avatarPath, "");
  const country = identity.country || relationship?.country || notSupplied;
  const relationshipSource = relationship?.source || relationship?.relationshipSource;
  const lawfulBasis = relationship?.lawfulBasisState;
  const statusValue = relationship?.status || "draft";
  const status = c(`status.${statusValue}`, formatPatientStatus(statusValue));
  const lawfulBasisOptions = LAWFUL_BASIS_VALUES.map((value) => ({ value, label: c(`lawfulBasis.${value}`, formatPatientStatus(value)) }));
  const activeFacilities = (workspace?.patientFacilities || []).filter((facility) => !facility.status || facility.status === "active");
  const facilityOptions = activeFacilities.map((facility) => ({ value: facility.id, label: facility.name }));
  const originalDraft = relationshipDraftFor(relationship);
  const relationshipDirty = !sameRelationshipDraft(relationshipDraft, originalDraft);
  const canSaveRelationship = managing && relationshipDraft.facilityIds.length > 0 && Boolean(relationshipDraft.lawfulBasisState) && relationshipDirty && !relationshipBusy;
  const removed = statusValue === "removed";

  async function copyLink() {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      setNotice(c("notices.linkCopied", "Patient link copied."));
    } catch {
      setNotice(c("notices.linkCopyFallback", "Copy the Patient relationship URL from your browser address bar."));
    }
    setOverflowOpen(false);
  }

  function openInNewTab() {
    window.open(window.location.href, "_blank", "noopener,noreferrer");
    setOverflowOpen(false);
  }

  function startManaging() {
    setRelationshipDraft(relationshipDraftFor(relationship));
    setManaging(true);
    setOverflowOpen(false);
  }

  function cancelManaging() {
    setRelationshipDraft(relationshipDraftFor(relationship));
    setManaging(false);
  }

  async function saveRelationship() {
    if (!canSaveRelationship) return;
    setRelationshipBusy(true);
    try {
      await updateInstitutionPatientRelationship({
        institutionId,
        relationshipId: relationship.id,
        relationship: {
          facilityIds: relationshipDraft.facilityIds,
          lawfulBasisState: relationshipDraft.lawfulBasisState,
        },
      });
      await onRefresh?.();
      setManaging(false);
      setNotice(c("notices.relationshipUpdated", "Institution relationship updated."));
    } catch {
      setNotice(c("errors.relationshipUpdate", "The Institution relationship could not be updated. Please try again."));
    } finally {
      setRelationshipBusy(false);
    }
  }

  const headerActions = managing ? <>
    <Button type="button" variant="ghost" disabled={relationshipBusy} onClick={cancelManaging}>{c("actions.cancel", "Cancel")}</Button>
    <Button type="button" variant="default" loading={relationshipBusy} disabled={!canSaveRelationship} onClick={saveRelationship}>{c("actions.save", "Save")}</Button>
  </> : <div className="institution-clinician-overflow-wrap institution-patient-overflow-wrap" ref={overflowRef}>
    <button ref={overflowTriggerRef} type="button" className="institutions-icon-button institution-clinician-more-button" aria-label={c("actions.more", "More Patient actions")} aria-expanded={overflowOpen} aria-haspopup="menu" onClick={() => setOverflowOpen((current) => !current)}><img src={MORE_ICON} alt="" /></button>
    {overflowOpen ? <div className={`institution-clinician-overflow-menu institution-patient-overflow-menu${overflowMenuPosition ? " institution-patient-overflow-menu--viewport" : ""}`} style={overflowMenuPosition || undefined} role="menu" aria-label={c("actions.relationship", "Patient relationship actions")}>
      <button type="button" role="menuitem" onClick={copyLink}><PatientActionIcon src={LINK_ICON} />{c("actions.copyLink", "Copy link")}</button>
      <button type="button" role="menuitem" onClick={openInNewTab}><PatientActionIcon src={NEW_TAB_ICON} />{c("actions.openInNewTab", "Open in new tab")}</button>
      {!removed ? <button type="button" role="menuitem" disabled={profileLoading} onClick={() => { setOverflowOpen(false); refreshProfile({ announce: true }); }}><PatientActionIcon src={REFRESH_ICON} />{profileLoading ? c("actions.refreshing", "Refreshing Patient profile…") : c("actions.refresh", "Refresh Patient profile")}</button> : null}
      {!removed ? <button type="button" role="menuitem" onClick={startManaging}><PatientActionIcon src={MANAGE_ICON} />{c("actions.manage", "Manage relationship")}</button> : null}
    </div> : null}
  </div>;

  const patientCover = isDark ? PATIENT_COVER_DARK : PATIENT_COVER_LIGHT;

  return <section className="institution-patient-detail institution-patient-editor" aria-label={c("ariaLabel", "Patient detail")}>
    <InstitutionProfileCover
      src={patientCover}
      fallbackSrc={patientCover}
      avatarSrc={avatarUrl}
      avatarAlt={title}
      avatarRailClassName="afrimed-page"
      avatarPlaceholderSrc="/icons/Profile_Avatar_borderless.svg"
    >
      <InstitutionPatientPageHeader className="mt-16 md:mt-20 lg:mt-24" title={title} showTitle={false} onBack={() => navigate(patientPath(institutionId))} actions={headerActions} />
    </InstitutionProfileCover>
    <div className="afrimed-rail afrimed-page pt-4 pb-12">
      <div className="relative min-h-[40px]" aria-hidden="true" />
      <div className="mt-2 md:mt-14">
        <h1 className="text-headline-small text-on-surface">{identityName}</h1>
        <p className="mt-1 text-body-medium text-on-surface-var">{identity.patientReference || relationship?.patientReference || c("patient", "Patient")}</p>
      </div>
      <div className="institution-patient-editor-body mt-5">
      <div className="institution-patient-detail-section-stack">
        <PatientSection title={c("sections.basicInformation", "Basic Information")}>
          <InstitutionPatientReadOnlyField label={c("fields.title", "Title")} value={identity.title || relationship?.title} />
          <InstitutionPatientReadOnlyField label={c("fields.firstName", "First name")} value={identity.firstName || relationship?.firstName} />
          <InstitutionPatientReadOnlyField label={c("fields.lastName", "Last name")} value={identity.lastName || relationship?.lastName} />
          <InstitutionPatientReadOnlyField label={c("fields.dateOfBirth", "Date of birth")} value={formatDate(identity.dateOfBirth || relationship?.dateOfBirth, notSupplied)} />
          <InstitutionPatientReadOnlyField label={c("fields.gender", "Gender")} value={identity.gender || relationship?.gender || notSupplied} />
          <InputPhoneSADC className="institution-patient-phone" value={phone.local} cc={phone.cc} countryIso={phone.countryIso} tone="surface" countryCodeLabel={c("fields.countryCode", "Country code")} numberLabel={c("fields.phoneNumber", "Phone number")} readOnly />
          <InstitutionPatientReadOnlyField label={c("fields.email", "Email")} value={identity.email || relationship?.email} />
          <InstitutionPatientReadOnlyField label={c("fields.nationalId", "National ID / Passport")} value={identity.nationalId || (relationship?.nationalIdPresent ? c("idDocumentOnFile", "ID document on file") : notSupplied)} />
          <InstitutionPatientReadOnlyField label={c("fields.patientId", "Patient ID")} value={identity.patientReference || relationship?.patientReference || notSupplied} />
          <InstitutionPatientReadOnlyField label={c("fields.idVerification", "ID verification")} value={c(`idVerification.${identity.idVerificationStatus || "not_recorded"}`, formatPatientStatus(identity.idVerificationStatus || "not_recorded"))} />
        </PatientSection>

        <PatientSection title={c("sections.physicalAddress", "Physical Address")}>
          <InstitutionPatientReadOnlyField label={c("fields.address", "Address")} value={identity.address || relationship?.address} />
          <InstitutionPatientReadOnlyField label={c("fields.city", "City")} value={identity.city || relationship?.city} />
          <InstitutionPatientReadOnlyField label={c("fields.postalCode", "Postal code")} value={identity.postalCode || relationship?.postalCode} />
          <InstitutionPatientReadOnlyField label={c("fields.country", "Country")} value={country} leadingSlot={countryFlag(country, patientPhone) ? <img className="institution-patient-readonly-field__flag" src={countryFlag(country, patientPhone)} alt="" /> : null} />
        </PatientSection>

        <PatientSection title={c("sections.relationship", "Institution relationship")}>
          {managing ? <InstitutionPatientAnchoredSelect id="patient-detail-facilities" label={c("fields.assignedFacilities", "Assigned facilities")} values={relationshipDraft.facilityIds} options={facilityOptions} multiple showSelectedChips={false} onValuesChange={(facilityIds) => setRelationshipDraft((current) => ({ ...current, facilityIds }))} /> : <InstitutionPatientReadOnlyField label={c("fields.assignedFacilities", "Assigned facilities")} value={facilities} multiline />}
          <InstitutionPatientReadOnlyField label={c("fields.status", "Status")}><InstitutionLifecycleBadge status={statusValue} label={status} classPrefix="institutions-patient-status" /></InstitutionPatientReadOnlyField>
          <InstitutionPatientReadOnlyField label={c("fields.relationshipSource", "Relationship source")} value={relationshipSource ? c(`relationshipSources.${relationshipSource}`, formatPatientStatus(relationshipSource)) : notRecorded} />
          {managing ? <InstitutionPatientAnchoredSelect id="patient-detail-lawful-basis" label={c("fields.lawfulBasis", "Lawful basis")} value={relationshipDraft.lawfulBasisState} options={lawfulBasisOptions} onChange={(lawfulBasisState) => setRelationshipDraft((current) => ({ ...current, lawfulBasisState }))} /> : <InstitutionPatientReadOnlyField label={c("fields.lawfulBasis", "Lawful basis")} value={lawfulBasis ? c(`lawfulBasis.${lawfulBasis}`, formatPatientStatus(lawfulBasis)) : notRecorded} />}
        </PatientSection>

        {profile?.healthProfileAccess ? <PatientSection title={c("sections.healthProfile", "Health profile")}>
          <InstitutionPatientReadOnlyField label={c("fields.languages", "Languages")} value={health.languages?.join(", ")} multiline />
          <InstitutionPatientReadOnlyField label={c("fields.bloodType", "Blood type")} value={health.bloodType} />
          <InstitutionPatientReadOnlyField label={c("fields.allergies", "Allergies")} value={health.allergies?.join(", ")} multiline />
          <InstitutionPatientReadOnlyField label={c("fields.medicalConditions", "Medical conditions")} value={health.conditions?.join(", ")} multiline />
          <InstitutionPatientReadOnlyField label={c("fields.medications", "Medications")} value={medicationLabels(health.medications)} multiline />
          <InstitutionPatientReadOnlyField label={c("fields.organDonor", "Organ donor")} value={health.organDonor} />
        </PatientSection> : <p className="institution-patient-notice" role="status">{profileError || c("healthProfileUnavailable", "Health profile access is not available for this relationship.")}</p>}
      </div>
      </div>
    </div>
    <Snackbar open={Boolean(notice)} message={notice} onClose={() => setNotice("")} />
  </section>;
}
